/**
 * Seo — runtime document-head manager (no extra deps)
 * ────────────────────────────────────────────────────
 * The build step (scripts/postbuild-seo.mjs) bakes the same tags statically
 * into each route's HTML for crawlers and social bots; this keeps the head
 * correct during client-side navigation and locale toggles. Selectors here
 * must stay in sync with renderHead() in seo/routes.js.
 *
 * Usage:
 *   <Seo route="pricing" />                          // manifest route (single-return pages)
 *   useSeo('app')                                    // hook form (multi-return pages)
 *   useSeo(null, { title: {ar,en}, noindex: true })  // fully custom
 *   useSeo(null, { skip: true })                     // no-op until data is ready
 */

import { useEffect } from 'react';
import { useLocale } from '../lib/i18n';
import { getHead, SITE } from '../seo/routes.js';
import { graph, webPage } from '../seo/schema.js';
import { revealPrerender } from '../lib/prerender.js';

const pick = (v, locale) => (v && typeof v === 'object' && ('ar' in v || 'en' in v) ? v[locale] ?? v.ar : v);

function upsert(selector, create, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}

const setMeta = (value, content, nameAttr = 'name') =>
  upsert(`meta[${nameAttr}="${value}"]`, () => {
    const el = document.createElement('meta');
    el.setAttribute(nameAttr, value);
    return el;
  }, { content });

export function applyHead(head) {
  document.title = head.title;
  setMeta('description', head.description);

  const robots = document.head.querySelector('meta[name="robots"]');
  if (head.noindex) setMeta('robots', 'noindex, nofollow');
  else if (robots) robots.remove();

  upsert('link[rel="canonical"]', () => {
    const el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    return el;
  }, { href: head.canonical });

  setMeta('og:site_name', SITE.name, 'property');
  setMeta('og:type', head.ogType, 'property');
  setMeta('og:url', head.canonical, 'property');
  setMeta('og:title', head.title, 'property');
  setMeta('og:description', head.description, 'property');
  setMeta('og:image', head.ogImage, 'property');
  setMeta('og:locale', head.locale === 'ar' ? 'ar_SA' : 'en_US', 'property');
  setMeta('og:locale:alternate', head.locale === 'ar' ? 'en_US' : 'ar_SA', 'property');
  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', head.title);
  setMeta('twitter:description', head.description);
  setMeta('twitter:image', head.ogImage);

  let ld = document.getElementById('seo-jsonld');
  if (!ld) {
    ld = document.createElement('script');
    ld.id = 'seo-jsonld';
    ld.type = 'application/ld+json';
    document.head.appendChild(ld);
  }
  ld.textContent = JSON.stringify(head.jsonld);
}

export function useSeo(route, overrides = {}) {
  const locale = useLocale();
  // Overrides are plain data — a serialized dep keeps the effect stable
  // across re-renders without asking callers to memoize.
  const depKey = JSON.stringify([route, locale, overrides]);

  useEffect(() => {
    if (overrides.skip) return;
    let head;
    if (route) {
      head = getHead(route, locale, {
        ...(overrides.title != null ? { title: pick(overrides.title, locale) } : {}),
        ...(overrides.description != null ? { description: pick(overrides.description, locale) } : {}),
        ...(overrides.path != null ? { path: overrides.path } : {}),
        ...(overrides.ogImage != null ? { ogImage: overrides.ogImage } : {}),
        ...(overrides.ogType != null ? { ogType: overrides.ogType } : {}),
        ...(overrides.noindex != null ? { noindex: overrides.noindex } : {}),
        ...(overrides.extraJsonld ? { extraJsonld: overrides.extraJsonld } : {})
      });
    } else {
      const p = overrides.path || window.location.pathname;
      const t = pick(overrides.title, locale) || SITE.name;
      const d = pick(overrides.description, locale) || '';
      const img = overrides.ogImage
        ? (overrides.ogImage.startsWith('http') ? overrides.ogImage : `${SITE.origin}${overrides.ogImage}`)
        : `${SITE.origin}${SITE.ogDefault}`;
      head = {
        title: t,
        description: d,
        canonical: `${SITE.origin}${p}`,
        ogImage: img,
        ogType: overrides.ogType || 'website',
        noindex: !!overrides.noindex,
        locale,
        jsonld: graph(webPage({ path: p, title: t, description: d, locale }), ...(overrides.extraJsonld || []))
      };
    }
    if (head) applyHead(head);
    // Next frame: after hydration's follow-up renders (e.g. the phone layout) have flushed.
    window.requestAnimationFrame(revealPrerender);
  }, [depKey]); // eslint-disable-line react-hooks/exhaustive-deps
}

export default function Seo({ route, ...overrides }) {
  useSeo(route, overrides);
  return null;
}
